import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoaderComponent } from './loader.component';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private dialog = inject(MatDialog);
  private loaderRef?: MatDialogRef<LoaderComponent>;
  openedInstance = new BehaviorSubject<number>(0);
  private loaderCount = 0;

  /**
   * Show loader - tracks multiple simultaneous requests
   * @param messageText - Plain text message to display
   * @param messageHTML - HTML message to display (overrides messageText if provided)
   */
  openLoader(
    messageText: string = 'Loading...',
    messageHTML: string = ''
  ): MatDialogRef<LoaderComponent> | undefined {
    this.loaderCount++;

    // If loader already exists, just update the count and message
    if (this.loaderRef) {
      this.loaderRef.componentInstance.messageText = messageText;
      this.loaderRef.componentInstance.messageHTML = messageHTML;
      return this.loaderRef;
    }

    // Create new loader instance
    this.loaderRef = this.dialog.open(LoaderComponent, {
      disableClose: true,
      panelClass: 'transparent-loader',
      backdropClass: 'transparent-loader-backdrop',
      data: {
        messageText: messageText || 'Loading...',
        messageHTML: messageHTML || ''
      }
    });

    // Clean up reference when dialog is manually closed
    this.loaderRef.afterClosed().subscribe(() => {
      this.loaderRef = undefined;
      this.loaderCount = 0;
    });

    return this.loaderRef;
  }

  /**
   * Hide the current loader only when all requests are complete
   */
  closeLoader(): void {
    this.loaderCount--;

    // Only close when all requests are done
    if (this.loaderCount <= 0) {
      this.loaderCount = 0;

      if (this.loaderRef) {
        this.loaderRef.close();
        this.loaderRef = undefined;

        // Force close all loader dialogs if multiple exist
        setTimeout(() => {
          const openLoaders = this.dialog.openDialogs.filter(
            d => d.componentInstance instanceof LoaderComponent
          );
          if (openLoaders.length > 0) {
            openLoaders.forEach(d => d.close());
          }
        }, 100);
      } else {
        // Close any orphaned loaders
        const openLoaders = this.dialog.openDialogs.filter(
          d => d.componentInstance instanceof LoaderComponent
        );
        if (openLoaders.length > 0) {
          openLoaders.forEach(d => d.close());
        }
      }
    }
  }

  /**
   * Check if loader is currently visible
   */
  isVisible(): boolean {
    return this.loaderRef !== undefined && this.loaderRef !== null;
  }

  /**
   * Update the message of the current loader
   * @param messageText - New plain text message
   * @param messageHTML - New HTML message (overrides messageText if provided)
   */
  updateMessage(messageText: string = '', messageHTML: string = ''): void {
    if (this.loaderRef) {
      this.loaderRef.componentInstance.messageText = messageText;
      this.loaderRef.componentInstance.messageHTML = messageHTML;
    }
  }

  /**
   * Force close the loader (use sparingly, e.g., on navigation or error recovery)
   */
  forceClose(): void {
    this.loaderCount = 0;
    if (this.loaderRef) {
      this.loaderRef.close();
      this.loaderRef = undefined;
    }
  }

  /**
   * Get current request count (useful for debugging)
   */
  getLoaderCount(): number {
    return this.loaderCount;
  }
}
